/* ══════════════════════════════════════════════════════════════
   Equity Curve — SVG Equity Curve Visualization
   ══════════════════════════════════════════════════════════════ */

export interface EquityCurveData {
    values: number[];
    benchmarkValues?: number[];
    startDate: string;
    endDate: string;
    initialCapital: number;
}

export function renderEquityCurve(container: HTMLElement, data?: EquityCurveData): void {
    const d: EquityCurveData = data || {
        values: Array.from({ length: 252 }, (_, i) => 1000000 * (1 + Math.sin(i / 30) * 0.05 + i * 0.0003)),
        benchmarkValues: Array.from({ length: 252 }, (_, i) => 1000000 * (1 + i * 0.0002)),
        startDate: '2024-03-01', endDate: '2025-02-26', initialCapital: 1000000,
    };

    const w = 600, h = 200, pad = 40;
    const min = Math.min(...d.values, ...(d.benchmarkValues || []));
    const max = Math.max(...d.values, ...(d.benchmarkValues || []));
    const range = max - min || 1;
    const step = (w - pad * 2) / (d.values.length - 1);

    const toPoint = (v: number, i: number) => `${pad + i * step},${h - pad - ((v - min) / range * (h - pad * 2))}`;

    const strategyLine = d.values.map((v, i) => toPoint(v, i)).join(' ');
    const benchLine = d.benchmarkValues?.map((v, i) => toPoint(v, i)).join(' ') || '';

    const finalValue = d.values[d.values.length - 1];
    const totalReturn = ((finalValue - d.initialCapital) / d.initialCapital * 100).toFixed(1);
    const color = parseFloat(totalReturn) >= 0 ? 'var(--india-green)' : 'var(--india-red)';

    container.innerHTML = `
    <div class="equity-curve glass-card">
      <div class="card-header">
        <h3>📈 Equity Curve</h3>
        <div class="ec-stats">
          <span>₹${(d.initialCapital / 100000).toFixed(0)}L → ₹${(finalValue / 100000).toFixed(1)}L</span>
          <span style="color:${color}">${parseFloat(totalReturn) >= 0 ? '+' : ''}${totalReturn}%</span>
        </div>
      </div>
      <svg width="100%" viewBox="0 0 ${w} ${h}" class="ec-svg">
        ${benchLine ? `<polyline points="${benchLine}" fill="none" stroke="#666" stroke-width="1" stroke-dasharray="4" opacity="0.5"/>` : ''}
        <polyline points="${strategyLine}" fill="none" stroke="${color}" stroke-width="2"/>
        <text x="${pad}" y="${h - 5}" font-size="10" fill="#888">${d.startDate}</text>
        <text x="${w - pad}" y="${h - 5}" font-size="10" fill="#888" text-anchor="end">${d.endDate}</text>
      </svg>
      <div class="ec-legend">
        <span class="legend-item"><span class="legend-line strategy"></span>Strategy</span>
        ${d.benchmarkValues ? '<span class="legend-item"><span class="legend-line benchmark"></span>Buy & Hold</span>' : ''}
      </div>
    </div>`;
}
